import { useState, useEffect, useRef } from 'react';
import FAQ from "../components/FAQ";
import Footer from "../components/Footer";
import Header from "../components/Header";

function BookNow() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    serviceType: 'From Airport',
    pickupDate: '',
    pickupTime: '',
    pickupLocation: '',
    dropoffLocation: '',
    passengers: 1,
    luggage: 0,
    accessible: false,
    childSeat: false,
    selectedVehicle: null,
    bookingHours: null, // Will be set to minimum hours when vehicle is selected
    // Personal details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialInstructions: ''
  });

  const [showCurrentLocation, setShowCurrentLocation] = useState(false);
  const [showDropoffLocation, setShowDropoffLocation] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isGettingDropoffLocation, setIsGettingDropoffLocation] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [errors, setErrors] = useState({});

  // Autocomplete suggestions state
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [isLoadingPickup, setIsLoadingPickup] = useState(false);
  const [isLoadingDropoff, setIsLoadingDropoff] = useState(false);

  // Refs for autocomplete inputs
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupDebounceRef = useRef(null);
  const dropoffDebounceRef = useRef(null);

  // Fetch location suggestions from Nominatim (OpenStreetMap)
  const fetchLocationSuggestions = async (query, isPickup = true) => {
    if (!query || query.length < 1) {
      if (isPickup) {
        setPickupSuggestions([]);
        setShowPickupSuggestions(false);
      } else {
        setDropoffSuggestions([]);
        setShowDropoffSuggestions(false);
      }
      return;
    }

    if (isPickup) {
      setIsLoadingPickup(true);
    } else {
      setIsLoadingDropoff(true);
    }

    try {
      // Search within New York City area - STRICT NYC ONLY
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}, New York City, New York, USA&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=20&` +
        `countrycodes=us&` +
        `bounded=1&` +
        `viewbox=-74.3,40.4,-73.7,41.0` // Strict NYC bounding box
      );
      
      const data = await response.json();
      
      // STRICT filtering - only NYC area results
      const suggestions = data
        .filter(place => {
          const addr = place.address;
          const lat = parseFloat(place.lat);
          const lon = parseFloat(place.lon);
          
          // MUST be in NYC bounding box
          const inNYCBox = lat >= 40.4 && lat <= 41.0 && lon >= -74.3 && lon <= -73.7;
          
          // MUST be in New York state
          const inNYState = addr && addr.state === 'New York';
          
          // MUST be in one of the 5 boroughs
          const inNYCBorough = addr && (
            addr.city === 'New York' ||
            addr.county === 'New York County' ||
            addr.county === 'Kings County' ||
            addr.county === 'Queens County' ||
            addr.county === 'Bronx County' ||
            addr.county === 'Richmond County' ||
            addr.city_district === 'Manhattan' ||
            addr.city_district === 'Brooklyn' ||
            addr.city_district === 'Queens' ||
            addr.city_district === 'Bronx' ||
            addr.city_district === 'Staten Island'
          );
          
          // MUST NOT be from other countries
          const isUSA = addr && addr.country_code === 'us';
          
          return inNYCBox && inNYState && inNYCBorough && isUSA;
        })
        .map(place => {
          const addr = place.address;
          let shortName = '';
          let area = '';
          let locationType = '';
          
          // Determine location type and format name
          if (place.type === 'aerodrome' || addr.aerodrome) {
            // Airport
            shortName = place.name || addr.aerodrome || 'Airport';
            locationType = 'Airport';
            area = addr.suburb || addr.county?.replace(' County', '') || 'New York';
          } else if (addr.road && addr.house_number) {
            // Street address
            shortName = `${addr.house_number} ${addr.road}`;
            area = addr.neighbourhood || addr.suburb || addr.city_district || '';
          } else if (addr.road) {
            // Street/Road
            shortName = addr.road;
            area = addr.neighbourhood || addr.suburb || addr.city_district || '';
          } else if (addr.neighbourhood) {
            // Neighborhood
            shortName = addr.neighbourhood;
            area = addr.suburb || addr.city_district || '';
          } else if (addr.suburb) {
            // Suburb/Area
            shortName = addr.suburb;
            area = addr.city_district || addr.county?.replace(' County', '') || '';
          } else if (place.name) {
            // Named location (landmark, building, etc.)
            shortName = place.name;
            area = addr.suburb || addr.neighbourhood || addr.city_district || '';
          } else {
            // Fallback
            shortName = place.display_name.split(',')[0];
            area = addr.city_district || '';
          }
          
          // Clean up area name - convert county names to borough names
          if (area) {
            area = area.replace(' County', '');
            if (area === 'Kings') area = 'Brooklyn';
            if (area === 'Richmond') area = 'Staten Island';
            if (area === 'New York' && addr.city_district) area = addr.city_district;
          }
          
          return {
            display_name: place.display_name,
            short_name: shortName,
            area: area,
            type: locationType || place.type,
            lat: parseFloat(place.lat),
            lon: parseFloat(place.lon),
            address: place.address
          };
        })
        // Remove duplicates based on short_name
        .filter((suggestion, index, self) => 
          index === self.findIndex(s => s.short_name === suggestion.short_name && s.area === suggestion.area)
        )
        .slice(0, 8); // Show up to 8 results

      if (isPickup) {
        setPickupSuggestions(suggestions);
        setShowPickupSuggestions(suggestions.length > 0);
        setIsLoadingPickup(false);
      } else {
        setDropoffSuggestions(suggestions);
        setShowDropoffSuggestions(suggestions.length > 0);
        setIsLoadingDropoff(false);
      }
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      if (isPickup) {
        setIsLoadingPickup(false);
      } else {
        setIsLoadingDropoff(false);
      }
    }
  };

  // Handle pickup location input change with debounce
  const handlePickupLocationChange = (value) => {
    setFormData(prev => ({
      ...prev,
      pickupLocation: value
    }));

    // Clear error
    if (errors.pickupLocation) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.pickupLocation;
        return newErrors;
      });
    }

    // Debounce API calls
    if (pickupDebounceRef.current) {
      clearTimeout(pickupDebounceRef.current);
    }

    pickupDebounceRef.current = setTimeout(() => {
      fetchLocationSuggestions(value, true);
    }, 200);
  };

  // Handle dropoff location input change with debounce
  const handleDropoffLocationChange = (value) => {
    setFormData(prev => ({
      ...prev,
      dropoffLocation: value
    }));

    // Clear error
    if (errors.dropoffLocation) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.dropoffLocation;
        return newErrors;
      });
    }

    // Debounce API calls
    if (dropoffDebounceRef.current) {
      clearTimeout(dropoffDebounceRef.current);
    }

    dropoffDebounceRef.current = setTimeout(() => {
      fetchLocationSuggestions(value, false);
    }, 200);
  };

  // Select pickup suggestion
  const selectPickupSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      pickupLocation: suggestion.display_name
    }));
    setPickupCoords({ lat: suggestion.lat, lng: suggestion.lon });
    setPickupSuggestions([]);
    setShowPickupSuggestions(false);
    setMapKey(prev => prev + 1);
    console.log('✅ Pickup location selected:', suggestion.display_name);
  };

  // Select dropoff suggestion
  const selectDropoffSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      dropoffLocation: suggestion.display_name
    }));
    setDropoffCoords({ lat: suggestion.lat, lng: suggestion.lon });
    setDropoffSuggestions([]);
    setShowDropoffSuggestions(false);
    console.log('✅ Dropoff location selected:', suggestion.display_name);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // If selecting a vehicle, set default booking hours to minimum hours
      if (field === 'selectedVehicle') {
        const selectedVehicle = vehicles.find(v => v.id === value);
        if (selectedVehicle) {
          newData.bookingHours = selectedVehicle.minHours;
        }
      }
      
      return newData;
    });
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getCurrentLocation = () => {
    console.log('📍 Get Current Location button clicked!');
    setIsGettingLocation(true);
    
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      alert('Geolocation is not supported by your browser. Please enter your address manually.');
      setIsGettingLocation(false);
      setShowCurrentLocation(false);
      return;
    }

    console.log('🔄 Requesting location from browser...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        console.log('✅ Location obtained:', latitude, longitude);
        
        // Set coordinates for map
        setPickupCoords({ lat: latitude, lng: longitude });
        
        // Use Nominatim for reverse geocoding (free, no API key)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?` +
            `lat=${latitude}&` +
            `lon=${longitude}&` +
            `format=json&` +
            `addressdetails=1`
          );
          const data = await response.json();
          
          if (data && data.display_name) {
            const address = data.display_name;
            console.log('📍 Address found:', address);
            setFormData(prev => ({
              ...prev,
              pickupLocation: address
            }));
            setIsGettingLocation(false);
            setShowCurrentLocation(false);
            return;
          }
        } catch (error) {
          console.log('⚠️ Reverse geocoding failed, trying alternative...', error);
        }
        
        // Fallback to BigDataCloud
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const data = await response.json();
          
          let address = '';
          if (data.locality && data.principalSubdivision) {
            address = `${data.locality}, ${data.principalSubdivision}`;
          } else if (data.city) {
            address = data.city;
          } else {
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
          
          console.log('📍 Address found (BigDataCloud):', address);
          setFormData(prev => ({
            ...prev,
            pickupLocation: address
          }));
        } catch (error) {
          console.log('⚠️ All geocoding failed, using coordinates', error);
          const address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          setFormData(prev => ({
            ...prev,
            pickupLocation: address
          }));
        }
        
        setIsGettingLocation(false);
        setShowCurrentLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your current location. ';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Location permission was denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable. Please check if location services are enabled on your device.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. This might be due to:\n\n';
            errorMessage += '• Slow GPS signal\n';
            errorMessage += '• Location services disabled\n';
            errorMessage += '• Poor internet connection\n\n';
            errorMessage += 'Please try again or enter your address manually.';
            break;
          default:
            errorMessage += 'An unknown error occurred. Please enter your address manually.';
        }
        
        alert(errorMessage);
        setIsGettingLocation(false);
        setShowCurrentLocation(false);
      },
      {
        enableHighAccuracy: false, // Changed to false for faster response
        timeout: 30000, // Increased to 30 seconds
        maximumAge: 300000 // Allow cached location up to 5 minutes old
      }
    );
  };

  const handlePickupLocationFocus = () => {
    console.log('🔍 Pickup field focused - showing location button');
    setShowCurrentLocation(true);
  };

  const handlePickupLocationBlur = () => {
    console.log('👋 Pickup field blurred - hiding location button');
    setTimeout(() => setShowCurrentLocation(false), 300); // Increased from 200 to 300ms
  };

  const handleDropoffLocationFocus = () => {
    console.log('🔍 Dropoff field focused - showing location button');
    setShowDropoffLocation(true);
  };

  const handleDropoffLocationBlur = () => {
    console.log('👋 Dropoff field blurred - hiding location button');
    setTimeout(() => setShowDropoffLocation(false), 300);
  };

  const getDropoffLocation = () => {
    console.log('📍 Get Dropoff Location button clicked!');
    setIsGettingDropoffLocation(true);
    
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      alert('Geolocation is not supported by your browser. Please enter your address manually.');
      setIsGettingDropoffLocation(false);
      setShowDropoffLocation(false);
      return;
    }

    console.log('🔄 Requesting location from browser...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        console.log('✅ Location obtained:', latitude, longitude);
        
        // Set coordinates for map
        setDropoffCoords({ lat: latitude, lng: longitude });
        
        // Use Nominatim for reverse geocoding (free, no API key)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?` +
            `lat=${latitude}&` +
            `lon=${longitude}&` +
            `format=json&` +
            `addressdetails=1`
          );
          const data = await response.json();
          
          if (data && data.display_name) {
            const address = data.display_name;
            console.log('📍 Address found:', address);
            setFormData(prev => ({
              ...prev,
              dropoffLocation: address
            }));
            setIsGettingDropoffLocation(false);
            setShowDropoffLocation(false);
            return;
          }
        } catch (error) {
          console.log('⚠️ Reverse geocoding failed, trying alternative...', error);
        }
        
        // Fallback to BigDataCloud
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const data = await response.json();
          
          let address = '';
          if (data.locality && data.principalSubdivision) {
            address = `${data.locality}, ${data.principalSubdivision}`;
          } else if (data.city) {
            address = data.city;
          } else {
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
          
          console.log('📍 Address found (BigDataCloud):', address);
          setFormData(prev => ({
            ...prev,
            dropoffLocation: address
          }));
        } catch (error) {
          console.log('⚠️ All geocoding failed, using coordinates', error);
          const address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          setFormData(prev => ({
            ...prev,
            dropoffLocation: address
          }));
        }
        
        setIsGettingDropoffLocation(false);
        setShowDropoffLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your current location. ';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Location permission was denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable. Please check if location services are enabled on your device.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. This might be due to:\n\n';
            errorMessage += '• Slow GPS signal\n';
            errorMessage += '• Location services disabled\n';
            errorMessage += '• Poor internet connection\n\n';
            errorMessage += 'Please try again or enter your address manually.';
            break;
          default:
            errorMessage += 'An unknown error occurred. Please enter your address manually.';
        }
        
        alert(errorMessage);
        setIsGettingDropoffLocation(false);
        setShowDropoffLocation(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 300000
      }
    );
  };

  const geocodeAddress = async (address, isPickup = true) => {
    try {
      const response = await fetch(`https://api.bigdatacloud.net/data/forward-geocode?query=${encodeURIComponent(address)}&key=free`);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const location = data.results[0];
        const coords = { lat: location.latitude, lng: location.longitude };
        
        if (isPickup) {
          setPickupCoords(coords);
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };

  const incrementCounter = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + 1
    }));
  };

  const decrementCounter = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.max(0, prev[field] - 1)
    }));
  };

  const nextStep = () => {
    // Validate Step 1 fields before proceeding
    if (currentStep === 1) {
      const newErrors = {};
      
      if (!formData.serviceType) {
        newErrors.serviceType = 'Service type is required';
      }
      if (!formData.pickupDate) {
        newErrors.pickupDate = 'Pick-up date is required';
      }
      if (!formData.pickupTime) {
        newErrors.pickupTime = 'Pick-up time is required';
      }
      if (!formData.pickupLocation) {
        newErrors.pickupLocation = 'Pick-up location is required';
      }
      if (!formData.dropoffLocation) {
        newErrors.dropoffLocation = 'Drop-off location is required';
      }
      if (!formData.passengers || formData.passengers < 1) {
        newErrors.passengers = 'At least 1 passenger is required';
      }
      
      setErrors(newErrors);
      
      // If there are errors, don't proceed
      if (Object.keys(newErrors).length > 0) {
        return;
      }
    }
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // WhatsApp booking function
  const sendToWhatsApp = async () => {
    const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicle);
    
    // 1. Send email using Web3Forms (free, no signup)
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: '299fa7db-e0e1-4bc3-ae4c-ef0b91054c52',
          subject: `New Booking Request from ${formData.firstName} ${formData.lastName}`,
          from_name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          to_email: 'ahmadyasin7786@gmail.com',
          message: `
CUSTOMER DETAILS:
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}

RIDE DETAILS:
Service: ${formData.serviceType}
Date: ${formData.pickupDate}
Time: ${formData.pickupTime}
Pickup: ${formData.pickupLocation}
Dropoff: ${formData.dropoffLocation}
Passengers: ${formData.passengers}
Luggage: ${formData.luggage}

VEHICLE SELECTION:
Vehicle: ${selectedVehicle?.name || 'Not selected'}
Category: ${selectedVehicle?.category || 'N/A'}
Hourly Rate: ${selectedVehicle?.price || 'N/A'}
Booking Hours: ${formData.bookingHours || selectedVehicle?.minHours || 0} hours
Minimum Hours: ${selectedVehicle?.minHours || 0} hours

ADDITIONAL OPTIONS:
Accessible: ${formData.accessible ? 'Yes' : 'No'}
Child Seat: ${formData.childSeat ? 'Yes' : 'No'}

SPECIAL INSTRUCTIONS:
${formData.specialInstructions || 'None'}

PRICING BREAKDOWN:
Rate: ${selectedVehicle?.price || 'N/A'}
Booking Hours: ${formData.bookingHours || selectedVehicle?.minHours || 0} hours
Minimum Hours: ${selectedVehicle?.minHours || 0} hours
Total: ${calculateTotal()}

* Exclusive of Tolls and NYC State Taxation

Submitted: ${new Date().toLocaleString()}
          `
        })
      });

      const result = await response.json();
      if (result.success) {
        console.log('✅ Email sent successfully to ahmadyasin7786@gmail.com');
      } else {
        console.error('❌ Email failed:', result.message);
      }
    } catch (error) {
      console.error('❌ Email error:', error);
    }
    
    // 2. Send to WhatsApp
    let message = `📋 *NEW BOOKING REQUEST* 📋\n\n`;
    message += `👤 *Customer Details*\n`;
    message += `Name: ${formData.firstName} ${formData.lastName}\n`;
    message += `Email: ${formData.email}\n`;
    message += `Phone: ${formData.phone}\n\n`;
    
    message += `🚗 *Ride Details*\n`;
    message += `Service: ${formData.serviceType}\n`;
    message += `Date: ${formData.pickupDate}\n`;
    message += `Time: ${formData.pickupTime}\n`;
    message += `Pickup: ${formData.pickupLocation}\n`;
    message += `Dropoff: ${formData.dropoffLocation}\n`;
    message += `Passengers: ${formData.passengers}\n`;
    message += `Luggage: ${formData.luggage}\n\n`;
    
    message += `🚘 *Vehicle Selection*\n`;
    message += `Vehicle: ${selectedVehicle?.name || 'Not selected'}\n`;
    message += `Category: ${selectedVehicle?.category || 'N/A'}\n`;
    message += `Hourly Rate: ${selectedVehicle?.price || 'N/A'}\n`;
    message += `Minimum Hours: ${selectedVehicle?.minHours || 0} hours\n\n`;
    
    message += `⚙️ *Additional Options*\n`;
    message += `Accessible: ${formData.accessible ? 'Yes' : 'No'}\n`;
    message += `Child Seat: ${formData.childSeat ? 'Yes' : 'No'}\n`;
    
    if (formData.specialInstructions) {
      message += `\n📝 *Special Instructions*\n${formData.specialInstructions}\n`;
    }
    
    message += `\n💰 *Pricing Breakdown*\n`;
    message += `Rate: ${selectedVehicle?.price || 'N/A'}\n`;
    message += `Booking Hours: ${formData.bookingHours || selectedVehicle?.minHours || 0} hours\n`;
    message += `Minimum Hours: ${selectedVehicle?.minHours || 0} hours\n`;
    message += `*Total: ${calculateTotal()}*\n`;
    message += `_* Exclusive of Tolls, Fuel Surcharge and NYC State Taxation_\n\n`;
    message += `🕒 Submitted: ${new Date().toLocaleString()}`;
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = '17182004140';
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappURL, '_blank');
    
    alert('✅ Email sent to ahmadyasin7786@gmail.com!\n\nOpening WhatsApp. Please send the pre-filled message to confirm your booking.');
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if all required fields are filled
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.selectedVehicle) {
      alert('Please fill in all required fields before submitting.');
      return;
    }
    
    // Send to WhatsApp
    sendToWhatsApp();
  };

  const vehicles = [
    // Category: Sedan
    {
      id: 1,
      name: 'Volvo S90',
      slug: 'volvo-s90',
      category: 'Sedan',
      image: 'vol3.webp',
      passengers: 3,
      luggage: 3,
      hourlyRate: 90,
      minHours: 2,
      price: '$90/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Leather Seats']
    },
    {
      id: 2,
      name: 'Cadillac XT6',
      slug: 'cadillac-xt6',
      category: 'Sedan',
      image: 'xt61.webp',
      passengers: 3,
      luggage: 3,
      hourlyRate: 90,
      minHours: 2,
      price: '$90/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Premium Interior']
    },
    // Category: SUV
    {
      id: 3,
      name: 'GMC Denali',
      slug: 'gmc-denali',
      category: 'SUV',
      image: 'gmc1.jpg',
      passengers: 6,
      luggage: 6,
      hourlyRate: 120,
      minHours: 2,
      price: '$120/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Spacious Interior']
    },
    {
      id: 4,
      name: 'Chevrolet Suburban',
      slug: 'chevrolet-suburban',
      category: 'SUV',
      image: 'subu1.webp',
      passengers: 6,
      luggage: 6,
      hourlyRate: 120,
      minHours: 2,
      price: '$120/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Spacious Interior']
    },
    {
      id: 5,
      name: 'Lincoln Navigator',
      slug: 'lincoln-navigator',
      category: 'SUV',
      image: 'lin2.webp',
      passengers: 6,
      luggage: 6,
      hourlyRate: 120,
      minHours: 2,
      price: '$120/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Extra Space', 'Premium Sound']
    },
    // Category: Luxury SUV
    {
      id: 6,
      name: 'Cadillac Escalade',
      slug: 'cadillac-escalade',
      category: 'Luxury SUV',
      image: 'esc2.JPG',
      passengers: 6,
      luggage: 6,
      hourlyRate: 150,
      minHours: 2,
      price: '$150/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Extra Space', 'Premium Sound']
    },
    // Category: Luxury Sedan
    {
      id: 7,
      name: 'Mercedes S Class',
      slug: 'mercedes-s-class',
      category: 'Luxury Sedan',
      image: 'sclass1.webp',
      passengers: 3,
      luggage: 3,
      hourlyRate: 210,
      minHours: 2,
      price: '$210/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Premium Interior']
    },
    {
      id: 8,
      name: 'BMW 7 Series',
      slug: 'bmw-7-series',
      category: 'Luxury Sedan',
      image: 'bmw71.webp',
      passengers: 3,
      luggage: 3,
      hourlyRate: 210,
      minHours: 2,
      price: '$210/Hr',
      minBooking: 'Minimum 2 hrs booking',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Executive Comfort']
    },
    // Category: Premium Luxury Sedan
    {
      id: 9,
      name: 'Mercedes Maybach',
      slug: 'mercedes-maybach',
      category: 'Premium Luxury Sedan',
      image: 'may2.webp',
      passengers: 3,
      luggage: 3,
      hourlyRate: 420,
      minHours: 3,
      price: '$420/Hr',
      minBooking: 'Minimum 3 hrs booking',
      features: ['Professional Chauffeur', 'Champagne Service', 'Premium Interior', 'Red Carpet Service']
    },
    // Category: 8 Seater Limo
    {
      id: 10,
      name: 'Mercedes Sprinter 8 Seater',
      slug: 'mercedes-sprinter-8-seater',
      category: '8 Seater Limo',
      image: '82.jpg',
      passengers: 8,
      luggage: 8,
      hourlyRate: 210,
      minHours: 5,
      price: '$210/Hr',
      minBooking: 'Minimum 5 hrs booking: 3 hrs booking + 2 hrs garage time',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Group Travel', 'WiFi']
    },
    // Category: Bus
    {
      id: 11,
      name: 'Mercedes Sprinter 11 Seater',
      slug: 'mercedes-sprinter-11-seater',
      category: 'Bus',
      image: '111.webp',
      passengers: 11,
      luggage: 11,
      hourlyRate: 175,
      minHours: 5,
      price: '$175/Hr',
      minBooking: 'Minimum 5 hrs booking: 3 hrs booking + 2 hrs garage time',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Group Travel', 'WiFi']
    },
    {
      id: 12,
      name: 'Mercedes Sprinter 15 Seater',
      slug: 'mercedes-sprinter-15-seater',
      category: 'Bus',
      image: '152.png',
      passengers: 15,
      luggage: 15,
      hourlyRate: 200,
      minHours: 5,
      price: '$200/Hr',
      minBooking: 'Minimum 5 hrs booking: 3 hrs booking + 2 hrs garage time',
      features: ['Professional Chauffeur', 'Complimentary Water', 'Phone Charger', 'Group Travel', 'WiFi']
    }
  ];

  // Calculate total price based on selected vehicle
  const calculateTotal = () => {
    const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicle);
    if (!selectedVehicle) return '$0.00';
    
    // Use bookingHours if set, otherwise use minHours
    const hours = formData.bookingHours || selectedVehicle.minHours;
    const total = selectedVehicle.hourlyRate * hours;
    return `$${total.toFixed(2)}`;
  };

  // Get minimum hours for selected vehicle
  const getMinimumHours = () => {
    const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicle);
    return selectedVehicle ? selectedVehicle.minHours : 0;
  };

  return (
    <>
      <section 
        className="relative w-full min-h-[40vh] sm:min-h-[50vh] lg:min-h-[60vh] bg-cover bg-center"
        style={{
          backgroundImage: "url('blog12.png')",
        }}
      >
        <Header/>

        {/* Hero Content */}
        <div className="relative z-20 flex items-center justify-center min-h-[40vh] sm:min-h-[50vh] lg:min-h-[60vh] px-4">
          <div className="max-w-[1100px] text-center text-white">
            <h1 className="font-bold text-2xl sm:text-3xl lg:text-4xl leading-snug text-white">
              Book Your Ride
            </h1>
            <div className="mt-6 flex justify-center gap-2 text-sm font-medium">
              <a href="/" className="hover:text-logo-green">Home</a>
              <span>-</span>
              <span className="text-logo-green">Book Now</span>
            </div>
          </div>
        </div>
      </section>

      <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1100px] mx-auto">
          
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4 sm:space-x-8">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 1 ? 'bg-black text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block">Ride Info</span>
              </div>
              
              <div className={`w-8 sm:w-16 h-0.5 ${currentStep >= 2 ? 'bg-black' : 'bg-gray-300'}`}></div>
              
              <div className={`flex items-center ${currentStep >= 2 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 2 ? 'bg-black text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block">Select Vehicle</span>
              </div>
              
              <div className={`w-8 sm:w-16 h-0.5 ${currentStep >= 3 ? 'bg-black' : 'bg-gray-300'}`}></div>
              
              <div className={`flex items-center ${currentStep >= 3 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 3 ? 'bg-black text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  3
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block">Final Details</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={`grid grid-cols-1 ${currentStep === 1 ? 'lg:grid-cols-5' : 'lg:grid-cols-1'} gap-8`}>
              
              {/* Left Side - Form */}
              <div className={`${currentStep === 1 ? 'lg:col-span-3' : 'lg:col-span-1'} bg-white rounded-lg shadow-lg p-6 sm:p-8`}>
                
                {/* Step 1: Ride Info */}
                {currentStep === 1 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        1
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Step 1: Ride Info</h2>
                    </div>

                    <div className="space-y-6">
                      {/* Service Type */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Select Service Type
                        </label>
                        <select 
                          value={formData.serviceType}
                          onChange={(e) => handleInputChange('serviceType', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black ${
                            errors.serviceType ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="From Airport">From Airport</option>
                          <option value="To Airport">To Airport</option>
                          <option value="Hourly Service">Hourly Service</option>
                          <option value="Point to Point">Point to Point</option>
                          <option value="Wedding">Wedding</option>
                          <option value="Corporate">Corporate</option>
                        </select>
                        {errors.serviceType && (
                          <p className="mt-1 text-sm text-red-600">{errors.serviceType}</p>
                        )}
                      </div>

                      {/* Date and Time */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Pick-Up Date
                          </label>
                          <input 
                            type="date"
                            required
                            min={new Date().toISOString().split('T')[0]}
                            value={formData.pickupDate}
                            onChange={(e) => handleInputChange('pickupDate', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black ${
                              errors.pickupDate ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors.pickupDate && (
                            <p className="mt-1 text-sm text-red-600">{errors.pickupDate}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Pick-Up Time
                          </label>
                          <input 
                            type="time"
                            required
                            value={formData.pickupTime}
                            onChange={(e) => handleInputChange('pickupTime', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black ${
                              errors.pickupTime ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors.pickupTime && (
                            <p className="mt-1 text-sm text-red-600">{errors.pickupTime}</p>
                          )}
                        </div>
                      </div>

                      {/* Locations */}
                      <div className="relative">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Pick-up Location
                        </label>
                        <input 
                          ref={pickupInputRef}
                          type="text"
                          required
                          autoComplete="off"
                          value={formData.pickupLocation}
                          onChange={(e) => {
                            handlePickupLocationChange(e.target.value);
                          }}
                          onFocus={() => {
                            handlePickupLocationFocus();
                            if (formData.pickupLocation && pickupSuggestions.length > 0) {
                              setShowPickupSuggestions(true);
                            }
                          }}
                          onBlur={() => {
                            handlePickupLocationBlur();
                            setTimeout(() => setShowPickupSuggestions(false), 200);
                          }}
                          placeholder="Type NYC location (e.g., JFK, Times Square, Manhattan)"
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black ${
                            errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        
                        {/* Autocomplete Suggestions Dropdown */}
                        {showPickupSuggestions && pickupSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1 max-h-60 overflow-y-auto">
                            {pickupSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectPickupSuggestion(suggestion);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors border-b border-gray-100 last:border-b-0"
                              >
                                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {suggestion.short_name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {suggestion.area ? `${suggestion.area}, ` : ''}New York
                                    {suggestion.type === 'Airport' && ' • Airport'}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Loading indicator */}
                        {isLoadingPickup && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1">
                            <div className="px-4 py-3 text-center text-gray-500 text-sm">
                              <div className="inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2"></div>
                              Searching locations...
                            </div>
                          </div>
                        )}
                        
                        {/* Current Location Button */}
                        {showCurrentLocation && !showPickupSuggestions && !isLoadingPickup && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                getCurrentLocation();
                              }}
                              disabled={isGettingLocation}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                {isGettingLocation ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1">
                                <span className="text-blue-600 font-medium block">
                                  {isGettingLocation ? 'Getting your location...' : 'Use my current location'}
                                </span>
                                {isGettingLocation && (
                                  <span className="text-xs text-gray-500">Please wait...</span>
                                )}
                              </div>
                            </button>
                          </div>
                        )}
                        {errors.pickupLocation && (
                          <p className="mt-1 text-sm text-red-600">{errors.pickupLocation}</p>
                        )}
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Drop-Off Location
                        </label>
                        <input 
                          ref={dropoffInputRef}
                          type="text"
                          required
                          autoComplete="off"
                          value={formData.dropoffLocation}
                          onChange={(e) => {
                            handleDropoffLocationChange(e.target.value);
                          }}
                          onFocus={() => {
                            handleDropoffLocationFocus();
                            if (formData.dropoffLocation && dropoffSuggestions.length > 0) {
                              setShowDropoffSuggestions(true);
                            }
                          }}
                          onBlur={() => {
                            handleDropoffLocationBlur();
                            setTimeout(() => setShowDropoffSuggestions(false), 200);
                          }}
                          placeholder="Type NYC location (e.g., LaGuardia, Brooklyn, Queens)"
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black ${
                            errors.dropoffLocation ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        
                        {/* Autocomplete Suggestions Dropdown */}
                        {showDropoffSuggestions && dropoffSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1 max-h-60 overflow-y-auto">
                            {dropoffSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectDropoffSuggestion(suggestion);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors border-b border-gray-100 last:border-b-0"
                              >
                                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {suggestion.short_name}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {suggestion.area ? `${suggestion.area}, ` : ''}New York
                                    {suggestion.type === 'Airport' && ' • Airport'}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Loading indicator */}
                        {isLoadingDropoff && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1">
                            <div className="px-4 py-3 text-center text-gray-500 text-sm">
                              <div className="inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2"></div>
                              Searching locations...
                            </div>
                          </div>
                        )}
                        
                        {/* Current Location Button */}
                        {showDropoffLocation && !showDropoffSuggestions && !isLoadingDropoff && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-1">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                getDropoffLocation();
                              }}
                              disabled={isGettingDropoffLocation}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                {isGettingDropoffLocation ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1">
                                <span className="text-blue-600 font-medium block">
                                  {isGettingDropoffLocation ? 'Getting your location...' : 'Use my current location'}
                                </span>
                                {isGettingDropoffLocation && (
                                  <span className="text-xs text-gray-500">Please wait...</span>
                                )}
                              </div>
                            </button>
                          </div>
                        )}
                        {errors.dropoffLocation && (
                          <p className="mt-1 text-sm text-red-600">{errors.dropoffLocation}</p>
                        )}
                      </div>

                      {/* Passengers and Luggage */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Number of Passengers
                          </label>
                          <div className="flex items-center space-x-3">
                            <button 
                              type="button"
                              onClick={() => decrementCounter('passengers')}
                              className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50"
                            >
                              −
                            </button>
                            <span className="w-12 text-center font-semibold">{formData.passengers}</span>
                            <button 
                              type="button"
                              onClick={() => incrementCounter('passengers')}
                              className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50"
                            >
                              +
                            </button>
                          </div>
                          {errors.passengers && (
                            <p className="mt-1 text-sm text-red-600">{errors.passengers}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Luggage Count
                          </label>
                          <div className="flex items-center space-x-3">
                            <button 
                              type="button"
                              onClick={() => decrementCounter('luggage')}
                              className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50"
                            >
                              −
                            </button>
                            <span className="w-12 text-center font-semibold">{formData.luggage}</span>
                            <button 
                              type="button"
                              onClick={() => incrementCounter('luggage')}
                              className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Additional Options */}
                      <div className="space-y-3">
                        
                        
                        <div className="flex items-center space-x-3">
                          
                        </div>
                      </div>

                      <button 
                        type="button"
                        onClick={nextStep}
                        className="w-full bg-black hover:bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                      >
                        Select Vehicle
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Select Vehicle */}
                {currentStep === 2 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        2
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Step 2: Select Vehicle</h2>
                    </div>

                    

                    {/* Vehicle Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {vehicles.map((vehicle) => (
                        <div 
                          key={vehicle.id}
                          onClick={() => handleInputChange('selectedVehicle', vehicle.id)}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-lg ${
                            formData.selectedVehicle === vehicle.id 
                              ? 'border-black bg-gray-50 shadow-lg' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {/* Vehicle Image */}
                          <div className="relative mb-3">
                            <img 
                              src={vehicle.image} 
                              alt={vehicle.name}
                              className="w-full h-[200px] object-cover rounded-lg"
                            />
                            {formData.selectedVehicle === vehicle.id && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Vehicle Info */}
                          <div className="text-center">
                            <h3 className="font-bold text-gray-900 text-sm mb-1">{vehicle.name}</h3>
                            <p className="text-xs text-gray-500 mb-2">{vehicle.category}</p>
                            
                            {/* Capacity Icons */}
                            <div className="flex justify-center items-center space-x-4 mb-3">
                              <div className="flex items-center text-xs text-gray-600">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                {vehicle.passengers}
                              </div>
                              <div className="flex items-center text-xs text-gray-600">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
                                </svg>
                                {vehicle.luggage}
                              </div>
                            </div>

                            {/* Features */}
                            <div className="flex flex-wrap gap-1 mb-3 justify-center">
                              {vehicle.features.slice(0, 2).map((feature, index) => (
                                <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                                  {feature}
                                </span>
                              ))}
                            </div>

                            {/* Price and Request Quote */}
                            <div className="text-center">
                              <div className="text-lg font-bold text-gray-900 mb-1">{vehicle.price}</div>
                              <div className="text-xs font-bold text-black  mb-3">{vehicle.minBooking}</div>
                              <button 
                                type="button"
                                className={`w-full py-2 px-3 rounded-lg text-sm font-semibold transition-colors ${
                                  formData.selectedVehicle === vehicle.id
                                    ? 'bg-black text-white'
                                    : 'bg-gray-100 text-black hover:bg-gray-200'
                                }`}
                              >
                                {formData.selectedVehicle === vehicle.id ? 'Selected' : 'Request Quote'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex space-x-4 mt-6">
                      <button 
                        type="button"
                        onClick={prevStep}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition-colors"
                      >
                        Back
                      </button>
                      <button 
                        type="button"
                        onClick={nextStep}
                        disabled={!formData.selectedVehicle}
                        className="flex-1 bg-black hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                      >
                        Final Details
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Final Details */}
                {currentStep === 3 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        3
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Step 3: Final Details</h2>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            First Name *
                          </label>
                          <input 
                            type="text"
                            required
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Last Name *
                          </label>
                          <input 
                            type="text"
                            required
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input 
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input 
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Booking Hours *
                        </label>
                        <select
                          required
                          value={formData.bookingHours || ''}
                          onChange={(e) => handleInputChange('bookingHours', parseInt(e.target.value))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        >
                          <option value="">Select booking hours</option>
                          {formData.selectedVehicle && (() => {
                            const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicle);
                            if (!selectedVehicle) return null;
                            
                            const minHours = selectedVehicle.minHours;
                            const options = [];
                            
                            // Generate options from minimum hours up to 12 hours
                            for (let i = minHours; i <= 12; i++) {
                              // Special label for Sprinter vehicles (5 hour minimum) - ALL hours show garage time
                              if (minHours === 5) {
                                const bookingHours = i - 2; // Subtract 2 hours for garage time
                                options.push(
                                  <option key={i} value={i}>
                                    {i} hours ({bookingHours} hrs booking + 2 hrs garage time)
                                  </option>
                                );
                              } else {
                                options.push(
                                  <option key={i} value={i}>
                                    {i} hour{i > 1 ? 's' : ''}
                                  </option>
                                );
                              }
                            }
                            
                            return options;
                          })()}
                        </select>
                        {formData.selectedVehicle && (() => {
                          const selectedVehicle = vehicles.find(v => v.id === formData.selectedVehicle);
                          return selectedVehicle ? (
                            <p className="text-xs text-gray-500 mt-1">
                              Minimum booking: {selectedVehicle.minHours} hours
                            </p>
                          ) : null;
                        })()}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Special Instructions (Optional)
                        </label>
                        <textarea 
                          rows="3"
                          value={formData.specialInstructions}
                          onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                          placeholder="Any special requests or instructions..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        ></textarea>
                      </div>

                      {/* Booking Summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">Booking Summary</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Service:</span>
                            <span>{formData.serviceType}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Date & Time:</span>
                            <span>{formData.pickupDate} at {formData.pickupTime}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Passengers:</span>
                            <span>{formData.passengers}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Vehicle:</span>
                            <span>{vehicles.find(v => v.id === formData.selectedVehicle)?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Rate:</span>
                            <span>{vehicles.find(v => v.id === formData.selectedVehicle)?.price}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Booking Hours:</span>
                            <span>{formData.bookingHours || getMinimumHours()} hours</span>
                          </div>
                          <div className="flex justify-between font-semibold text-base pt-2 border-t">
                            <span>Total:</span>
                            <span>{calculateTotal()}</span>
                          </div>
                          
                          {/* Exclusions - Beautiful Format */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Exclusive of:</p>
                            <ul className="text-xs text-gray-600 space-y-1 ml-4">
                              <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>Tolls</span>
                              </li>
                              <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>Fuel Surcharge (5%)</span>
                              </li>
                              <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>NYC State Taxation</span>
                              </li>
                            </ul>
                            <div className="mt-3 p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                              <p className="text-xs text-blue-800">
                                <span className="font-semibold">Note:</span> This is your booking amount. Additional charges (tolls, fuel surcharge, and taxes) will be added after the ride.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-4">
                        <button 
                          type="button"
                          onClick={prevStep}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition-colors"
                        >
                          Back
                        </button>
                        <button 
                          type="submit"
                          className="flex-1 bg-black hover:bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                        >
                          Confirm Booking
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Map (40% width) - Only shown in Step 1 */}
              {currentStep === 1 && (
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden h-fit">
                    {/* Map Area - 500px height */}
                    <div className="h-[500px] relative">
                      {/* Google Maps iframe */}
                      {pickupCoords ? (
                        <iframe
                          key={`map-${pickupCoords.lat}-${pickupCoords.lng}`}
                          src={`https://maps.google.com/maps?q=${pickupCoords.lat},${pickupCoords.lng}&hl=en&z=14&output=embed`}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Your Current Location"
                          className="rounded-lg"
                        ></iframe>
                      ) : (
                        <iframe
                          src="https://www.google.com/maps?q=169+Munro+Blvd,+Valley+Stream,+NY+11581&output=embed"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Majestic Voyage USA - 169 Munro Blvd, Valley Stream, NY 11581"
                          className="rounded-lg"
                        ></iframe>
                      )}
                      
                      {/* Map Overlay */}
                      <div className="absolute top-4 left-4 bg-white bg-opacity-95 rounded-lg p-3 shadow-lg z-10">
                        <div className="text-center">
                          <div className="w-8 h-8 bg-blue-500 rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-sm font-semibold text-gray-700">
                            {pickupCoords ? 'Your Location' : 'Our Location'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {pickupCoords ? '📍 Current Position' : '169 Munro Blvd'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Location Status Badge */}
                      <div className="absolute bottom-4 right-4 bg-black bg-opacity-90 text-white rounded-lg px-3 py-2 z-10">
                        <p className="text-xs font-medium">
                          {pickupCoords 
                            ? '📍 Your Current Location' 
                            : '🏢 Majestic Voyage USA'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <FAQ/>
      <Footer/>
    </>
  );
}

export default BookNow;